
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { Clock, TrendingUp, AlertCircle } from "lucide-react";
import supabase from "@/utils/supabaseClient";
import { Progress } from "@/components/ui/progress";

interface PollOption {
  id: string;
  title: string;
  votes?: number;
  percentage?: number;
}

interface Poll {
  id: string;
  slug: string;
  title: string;
  imageUrl: string;
  totalVotes: number;
  options: Array<PollOption>;
}

interface RelatedPollsProps {
  currentPollId: string;
  categorySlug?: string;
}

interface PollWithTimer extends Poll {
  timeLeft: number;
  originalTime: number;
  leadingOption?: {
    title: string;
  };
}

const RelatedPollSkeleton = () => {
  return (
    <div className="flex flex-col space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4 p-3 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <Skeleton className="h-16 w-16 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1">
            <Skeleton className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 mb-2" />
            <Skeleton className="h-3 w-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const RelatedPolls = ({ currentPollId, categorySlug }: RelatedPollsProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [polls, setPolls] = useState<PollWithTimer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getRandomTime = () => Math.floor(Math.random() * 7) + 30;

  const findLeadingOption = async (pollId: string) => {
    try {
      // console.log(`Finding leading option for poll ${pollId}`)

      const response = await fetch(`${import.meta.env.VITE_API_URL}/polls/vote/summary/${pollId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch polls');
      }

      const data = await response.json();
      // console.log("Leading option data:", data)

      if (data && data.length > 0) {
        const { option_title } = data[0];
        // console.log(`Poll ${pollId} leading option: ${option_title}`)

        return {
          title: option_title
        };
      }

      return null;
    } catch (error) {
      // console.error("Error in findLeadingOption:", error);
      return null;
    }
  };

  const fetchSingleRandomPoll = async (index: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/polls?status=1`);

      if (!response.ok) {
        throw new Error('Failed to fetch polls');
      }

      const data = await response.json();

      const filteredPolls = data.filter((poll: Poll) => poll.id !== currentPollId);

      if (filteredPolls.length > 0) {
        const randomPoll = filteredPolls[Math.floor(Math.random() * filteredPolls.length)];
        const randomTime = getRandomTime();

        const leadingOption = await findLeadingOption(randomPoll.id);
        // console.log(`Poll ${randomPoll.id} leading option:`, leadingOption)

        setPolls(prevPolls => {
          const newPolls = [...prevPolls];
          newPolls[index] = {
            ...randomPoll,
            timeLeft: randomTime,
            originalTime: randomTime,
            leadingOption
          };
          return newPolls;
        });
      }
    } catch (error) {
      // console.error("Error fetching random poll:", error);
    }
  };

  useEffect(() => {
    const fetchRandomPolls = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${import.meta.env.VITE_API_URL}/polls?status=1`);

        if (!response.ok) {
          throw new Error('Failed to fetch polls');
        }

        const data = await response.json();

        const filteredPolls = data
          .filter((poll: Poll) => poll.id !== currentPollId)
          .sort(() => 0.5 - Math.random())
          .slice(0, 4);

        const pollsWithTimers = await Promise.all(filteredPolls.map(async (poll: Poll) => {
          const randomTime = getRandomTime();
          const leadingOption = await findLeadingOption(poll.id);
          // console.log(`Initial poll ${poll.id} leading option:`, leadingOption)

          return {
            ...poll,
            timeLeft: randomTime,
            originalTime: randomTime,
            leadingOption
          };
        }));

        setPolls(pollsWithTimers);
      } catch (error) {
        // console.error("Error fetching random polls:", error);
        setPolls([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRandomPolls();
  }, [currentPollId]);

  useEffect(() => {
    if (polls.length === 0) return;

    const timers = polls.map((_, index) => {
      return setInterval(() => {
        setPolls(prevPolls => {
          const newPolls = [...prevPolls];
          if (newPolls[index]) {
            if (newPolls[index].timeLeft <= 1) {
              fetchSingleRandomPoll(index);
            } else {
              newPolls[index] = {
                ...newPolls[index],
                timeLeft: newPolls[index].timeLeft - 1
              };
              return newPolls;
            }
          }
          return newPolls;
        });
      }, 1000);
    });

    return () => {
      timers.forEach(timer => clearInterval(timer));
    };
  }, [polls.length]);

  const handlePollClick = (slug: string) => {
    navigate(`/polls/${slug}`, { replace: false });
  };

  const formatTimeLeft = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <Card className="p-6 mb-8 dark:bg-gray-800/50 dark:border-gray-700">
      <h2 className="text-xl font-semibold mb-4 dark:text-white">You Might Also Like</h2>

      {isLoading ? (
        <RelatedPollSkeleton />
      ) : polls.length > 0 ? (
        <div className="flex flex-col space-y-4">
          {polls.map((poll, index) => (
            <div
              key={`${poll.id}-${index}`}
              onClick={() => handlePollClick(poll.slug)}
              className="flex items-center space-x-4 p-3 bg-white/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors cursor-pointer relative group border border-gray-200 dark:border-gray-700 shadow-sm"
            >
              <img
                src={poll.imageUrl || "https://via.placeholder.com/150"}
                alt={poll.title}
                className="h-16 w-16 object-cover rounded-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://via.placeholder.com/150";
                }}
              />
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-white line-clamp-1 mb-1">
                  {poll.title}
                </h3>
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                  <Clock className="h-3 w-3" />
                  <span className="font-medium">{formatTimeLeft(poll.timeLeft)}</span>
                  <span className="mx-1 opacity-50">•</span>
                  <span>{poll.totalVotes} votes</span>
                </div>

                {poll.leadingOption && (
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-primary" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                      {poll.leadingOption.title}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/30">
          <AlertCircle className="h-10 w-10 text-gray-400 dark:text-gray-500 mb-3" />
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">No Related Polls Yet</h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md">
            Stay tuned! More exciting polls will be appearing here soon. Check back later to discover new voting opportunities.
          </p>
        </div>
      )}
    </Card>
  );
};
